/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// @mui material components
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { logger } from "services/logger";

let loggedTypographyFallback = false;
let loggedFunctionFallback = false;
let loggedPaletteFallback = false;

const fallbackLinearGradient = (main = "#ffffff", state = "#ffffff", angle = 310) => {
  return `linear-gradient(${angle}deg, ${main}, ${state})`;
};

const debugContext = (theme, ownerState) => ({
  route: typeof window !== "undefined" ? window.location?.pathname : "server",
  ownerState,
  themeKeys: Object.keys(theme || {}),
  hasPalette: Boolean(theme?.palette),
  hasTypography: Boolean(theme?.typography),
  hasFunctions: Boolean(theme?.functions),
});

export default styled(Typography)(({ theme = {}, ownerState }) => {
  const palette = theme.palette || {};
  const typography = theme.typography || {};
  const functions = theme.functions || {};
  const { color, textTransform, verticalAlign, fontWeight, opacity, textGradient } = ownerState;

  if (!loggedPaletteFallback && (!palette || Object.keys(palette).length === 0)) {
    logger.error("[VuiTypography] palette ausente no tema. Aplicando fallback.", debugContext(theme, ownerState));
    loggedPaletteFallback = true;
  }

  if (!loggedTypographyFallback && (!typography || Object.keys(typography).length === 0)) {
    logger.error("[VuiTypography] typography ausente no tema. Aplicando fallback.", debugContext(theme, ownerState));
    loggedTypographyFallback = true;
  }

  if (!loggedFunctionFallback && (!functions || typeof functions.linearGradient !== "function")) {
    logger.error("[VuiTypography] functions.linearGradient ausente. Usando fallback.", debugContext(theme, ownerState));
    loggedFunctionFallback = true;
  }

  const {
    gradients = {},
    transparent = { main: "transparent" },
  } = palette;

  const {
    fontWeightLight = 300,
    fontWeightRegular = 400,
    fontWeightMedium = 500,
    fontWeightBold = 700,
  } = typography || {};

  const linearGradient =
    (functions && typeof functions.linearGradient === "function"
      ? functions.linearGradient
      : fallbackLinearGradient);

  // fontWeight styles
  const fontWeights = {
    light: fontWeightLight,
    regular: fontWeightRegular,
    medium: fontWeightMedium,
    bold: fontWeightBold,
  };

  // styles for the typography with textGradient={true}
  const gradientStyles = () => {
    const gradientSource =
      color !== "inherit" &&
      color !== "text" &&
      color !== "white" &&
      gradients &&
      gradients[color]
        ? gradients[color]
        : gradients?.primary || { main: "#ffffff", state: "#ffffff", deg: 310 };

    const { main = "#ffffff", state = "#ffffff", deg = 310 } = gradientSource;

    return {
      backgroundImage: linearGradient(main, state, deg),
      display: "inline-block",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: transparent?.main || "transparent",
      position: "relative",
      zIndex: 1,
    };
  };

  return {
    opacity,
    textTransform,
    verticalAlign,
    textDecoration: "none",
    color: color === "inherit" || !palette[color] ? "inherit" : palette[color].main,
    fontWeight: fontWeights[fontWeight] && fontWeights[fontWeight],
    ...(textGradient && gradientStyles()),
  };
});
